import { useChat, useSession } from '@livekit/components-react'
import {
  isKrispNoiseFilterSupported,
  KrispNoiseFilter,
} from '@livekit/krisp-noise-filter'
import { Button } from '@rekrytering/ui/components/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { Textarea } from '@rekrytering/ui/components/textarea'
import { useParams } from '@tanstack/react-router'
import { ConnectionState, Room, TokenSource } from 'livekit-client'
import { MessageSquare, Mic, Video } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { env } from '@/env.client'
import { AgentSessionProvider } from '@/features/interview/components/agents-ui/agent-session-provider'
import { AgentSessionView_01 } from '@/features/interview/components/agents-ui/blocks/agent-session-view-01/components/agent-session-block'
import { StartAudioButton } from '@/features/interview/components/agents-ui/start-audio-button'
import { useInterviewProgress } from '@/features/interview/hooks/use-interview-progress'

type Modality = 'video' | 'audio' | 'chat'

type ScreenerQuestion = {
  readonly _id: string
  readonly text: string
  readonly type: 'single_select' | 'multi_choice'
  readonly options: readonly string[]
  readonly disqualifyingAnswers: readonly string[]
  readonly order: number
}

type AdditionalInfoField = {
  readonly _id: string
  readonly label: string
  readonly placeholder: string
  readonly type: 'text' | 'number' | 'date' | 'url' | 'email'
  readonly required: boolean
  readonly order: number
}

type InterviewPayload = {
  readonly candidate: { readonly _id: string; readonly status: string }
  readonly job: {
    readonly title: string
    readonly expiryDays: number
    readonly language: string
    readonly introMessage: string | null | undefined
    readonly outroMessage: string | null | undefined
    readonly candidateFeedbackEnabled: boolean | null | undefined
    readonly allowedModalities: Modality[]
  }
  readonly orgSettings: {
    readonly displayName: string
    readonly termsUrl: string | null
    readonly privacyUrl: string | null
  }
  readonly branding: {
    readonly primaryColor: string | null
    readonly logoUrl: string | null
    readonly website: string | null
    readonly tagline: string | null
  }
  readonly screenerQuestions: ScreenerQuestion[]
  readonly additionalInfoFields: AdditionalInfoField[]
}

type PageState =
  | { readonly status: 'loading' }
  | { readonly status: 'invalid' }
  | { readonly status: 'org_locked' }
  | { readonly status: 'screener'; readonly payload: InterviewPayload }
  | { readonly status: 'additional-info'; readonly payload: InterviewPayload }
  | { readonly status: 'briefing'; readonly payload: InterviewPayload }
  | { readonly status: 'disqualified' }
  | {
      readonly status: 'room'
      readonly roomName: string
      readonly participantToken: string
      readonly outroMessage: string | null | undefined
      readonly candidateFeedbackEnabled: boolean | null | undefined
      readonly modality: 'voice' | 'text'
    }
  | {
      readonly status: 'completed'
      readonly token: string
      readonly outroMessage: string | null | undefined
      readonly candidateFeedbackEnabled: boolean | null | undefined
    }
  | { readonly status: 'abandoned' }
  | { readonly status: 'error'; readonly token: string }

const siteUrl = env.VITE_CONVEX_SITE_URL
const livekitWsUrl = env.VITE_LIVEKIT_WS_URL

const SUPPORTED_LANGUAGES = new Set(['sv', 'en', 'no', 'da', 'fi'])

const postInterviewAction = async (
  token: string,
  action: string,
  body: unknown,
): Promise<Response> => {
  return fetch(`${siteUrl}/interview/${token}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

const determineFirstStep = (
  payload: InterviewPayload,
): 'screener' | 'additional-info' | 'briefing' => {
  if (payload.screenerQuestions.length > 0) return 'screener'
  if (payload.additionalInfoFields.length > 0) return 'additional-info'
  return 'briefing'
}

const MODALITY_ICONS: Record<Modality, React.ElementType> = {
  video: Video,
  audio: Mic,
  chat: MessageSquare,
}

const ScreenerStep = ({
  payload,
  token,
  onPass,
  onDisqualified,
}: {
  payload: InterviewPayload
  token: string
  onPass: () => void
  onDisqualified: () => void
}) => {
  const { t } = useTranslation()
  const [answers, setAnswers] = useState<Record<string, string[]>>({})
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    setSubmitting(true)
    const formattedAnswers = payload.screenerQuestions.map((q) => ({
      questionId: q._id,
      selectedOptions: answers[q._id] ?? [],
    }))
    const res = await postInterviewAction(token, 'screener', {
      answers: formattedAnswers,
    })
    const data = await res.json()
    if (data.result === 'disqualified') {
      onDisqualified()
    } else {
      onPass()
    }
    setSubmitting(false)
  }

  const toggleOption = (questionId: string, option: string, multi: boolean) => {
    setAnswers((prev) => {
      const current = prev[questionId] ?? []
      if (multi) {
        return {
          ...prev,
          [questionId]: current.includes(option)
            ? current.filter((o) => o !== option)
            : [...current, option],
        }
      }
      return { ...prev, [questionId]: [option] }
    })
  }

  const allAnswered = payload.screenerQuestions.every(
    (q) => (answers[q._id] ?? []).length > 0,
  )

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">{t('candidate.screener.title')}</h2>
      {payload.screenerQuestions.map((q) => (
        <div key={q._id} className="space-y-2">
          <p className="font-medium">{q.text}</p>
          <div className="space-y-1">
            {q.options.map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type={q.type === 'multi_choice' ? 'checkbox' : 'radio'}
                  name={q._id}
                  checked={(answers[q._id] ?? []).includes(option)}
                  onChange={() =>
                    toggleOption(q._id, option, q.type === 'multi_choice')
                  }
                />
                {option}
              </label>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={handleSubmit}
        disabled={!allAnswered || submitting}
        className="px-4 py-2 bg-primary text-primary-foreground rounded disabled:opacity-50"
      >
        {t('candidate.screener.continue')}
      </button>
    </div>
  )
}

const AdditionalInfoStep = ({
  payload,
  token,
  onNext,
}: {
  payload: InterviewPayload
  token: string
  onNext: () => void
}) => {
  const { t } = useTranslation()
  const [values, setValues] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    setSubmitting(true)
    await postInterviewAction(token, 'additional-info', {
      additionalInfo: values,
    })
    onNext()
    setSubmitting(false)
  }

  const allRequiredFilled = payload.additionalInfoFields
    .filter((f) => f.required)
    .every((f) => (values[f.label] ?? '').trim().length > 0)

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">
        {t('candidate.additionalInfo.title')}
      </h2>
      {payload.additionalInfoFields.map((field) => (
        <div key={field._id} className="space-y-1">
          <label className="block font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </label>
          <input
            type={field.type}
            placeholder={field.placeholder}
            value={values[field.label] ?? ''}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, [field.label]: e.target.value }))
            }
            className="w-full border rounded px-3 py-2"
          />
        </div>
      ))}
      <button
        onClick={handleSubmit}
        disabled={!allRequiredFilled || submitting}
        className="px-4 py-2 bg-primary text-primary-foreground rounded disabled:opacity-50"
      >
        {t('candidate.additionalInfo.continue')}
      </button>
    </div>
  )
}

const BriefingStep = ({
  payload,
  token,
  onRoomReady,
  onError,
}: {
  payload: InterviewPayload
  token: string
  onRoomReady: (
    roomName: string,
    participantToken: string,
    selectedModality: Modality,
  ) => void
  onError: () => void
}) => {
  const { t } = useTranslation()
  const { allowedModalities } = payload.job

  const initialModality: Modality | null =
    allowedModalities.length === 1 ? (allowedModalities[0] ?? null) : null

  const [selectedModality, setSelectedModality] = useState<Modality | null>(
    initialModality,
  )
  const [consentChecked, setConsentChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [insufficientBalance, setInsufficientBalance] = useState(false)
  const [orgLocked, setOrgLocked] = useState(false)

  const handleReady = async () => {
    if (!selectedModality) return

    setSubmitting(true)
    setInsufficientBalance(false)
    setOrgLocked(false)

    const res = await postInterviewAction(token, 'consent', {
      selectedModality,
    })

    if (!res.ok) {
      onError()
      return
    }

    const data: {
      ok: boolean
      roomName?: string
      participantToken?: string
      reason?: string
    } = await res.json()

    if (!data.ok || !data.roomName || !data.participantToken) {
      if (data.reason === 'insufficient_balance') {
        setInsufficientBalance(true)
        setSubmitting(false)
      } else if (data.reason === 'account_locked') {
        setOrgLocked(true)
        setSubmitting(false)
      } else {
        onError()
      }
      return
    }

    onRoomReady(data.roomName, data.participantToken, selectedModality)
  }

  const primaryColor = payload.branding.primaryColor
  const isRecordingModality =
    selectedModality === 'video' || selectedModality === 'audio'
  const consentText = isRecordingModality
    ? t('candidate.briefing.consentRecording')
    : t('candidate.briefing.consentAi')

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        {payload.branding.logoUrl && (
          <img
            src={payload.branding.logoUrl}
            alt={payload.orgSettings.displayName}
            className="h-10 object-contain"
          />
        )}
        <div>
          <p className="text-sm text-muted-foreground">
            {payload.orgSettings.displayName}
          </p>
          <h2 className="text-2xl font-semibold">{payload.job.title}</h2>
        </div>
        {payload.branding.tagline && (
          <p className="text-sm text-muted-foreground italic">
            {payload.branding.tagline}
          </p>
        )}
        {payload.branding.website && (
          <a
            href={payload.branding.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm underline text-muted-foreground hover:text-foreground"
          >
            {payload.branding.website}
          </a>
        )}
      </div>

      {allowedModalities.length > 1 ? (
        <div className="space-y-3">
          <p className="text-sm font-medium">
            {t('candidate.briefing.howToConduct')}
          </p>
          <div className="grid gap-3">
            {allowedModalities.map((modality) => {
              const Icon = MODALITY_ICONS[modality]
              return (
                <button
                  key={modality}
                  type="button"
                  onClick={() => setSelectedModality(modality)}
                  className={`flex items-center gap-4 rounded-lg border p-4 text-left transition ${
                    selectedModality === modality
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <Icon className="size-5 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {t(`candidate.modality.${modality}.label`)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t(`candidate.modality.${modality}.description`)}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      ) : (
        selectedModality && (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
            {(() => {
              const Icon = MODALITY_ICONS[selectedModality]
              return (
                <>
                  <Icon className="size-4 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {t('candidate.briefing.usesModality')}{' '}
                    <span className="font-medium text-foreground">
                      {t(`candidate.modality.${selectedModality}.label`)}
                    </span>{' '}
                    — {t(`candidate.modality.${selectedModality}.description`)}.
                  </p>
                </>
              )
            })()}
          </div>
        )
      )}

      {selectedModality && (
        <>
          {payload.job.introMessage ? (
            <div
              className="prose prose-sm text-muted-foreground max-w-none [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4"
              dangerouslySetInnerHTML={{ __html: payload.job.introMessage }}
            />
          ) : (
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>{t('candidate.briefing.aboutToStart')}</p>
              <p>
                {t('candidate.briefing.expectedDuration', {
                  minutes: String(payload.job.expiryDays),
                })}
              </p>
              {isRecordingModality && (
                <p>{t('candidate.briefing.quietEnvironment')}</p>
              )}
            </div>
          )}

          <div className="rounded-md border border-border bg-muted/50 p-4 text-sm text-muted-foreground space-y-1">
            <p>{t('candidate.briefing.aiNotice')}</p>
            {env.VITE_SUPPORT_EMAIL && (
              <p>
                {t('candidate.briefing.requestScores')}{' '}
                <a
                  href={`mailto:${env.VITE_SUPPORT_EMAIL}`}
                  className="underline hover:text-foreground"
                >
                  {env.VITE_SUPPORT_EMAIL}
                </a>
                .
              </p>
            )}
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm">
              {consentText}
              {payload.orgSettings.termsUrl && (
                <>
                  {' '}
                  <a
                    href={payload.orgSettings.termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {t('candidate.briefing.termsOfService')}
                  </a>
                </>
              )}
              {payload.orgSettings.privacyUrl && (
                <>
                  {' '}
                  <a
                    href={payload.orgSettings.privacyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {t('candidate.briefing.privacyPolicy')}
                  </a>
                </>
              )}
            </span>
          </label>

          {insufficientBalance && (
            <p className="text-sm text-destructive">
              {t('candidate.briefing.insufficientBalance')}
            </p>
          )}

          {orgLocked && (
            <p className="text-sm text-destructive">
              {t('candidate.briefing.orgLockedError')}
            </p>
          )}

          <button
            onClick={handleReady}
            disabled={!consentChecked || submitting}
            style={primaryColor ? { backgroundColor: primaryColor } : undefined}
            className="px-6 py-3 bg-primary text-primary-foreground rounded font-medium disabled:opacity-50"
          >
            {submitting
              ? t('candidate.briefing.starting')
              : t('candidate.briefing.ready')}
          </button>
        </>
      )}
    </div>
  )
}

type CandidateCompetencyScore = {
  readonly competency: string
  readonly score: number
  readonly rationale: string
}

type CandidateFeedbackState =
  | { readonly status: 'loading' }
  | { readonly status: 'preparing' }
  | { readonly status: 'ready'; readonly scores: CandidateCompetencyScore[] }

const CompletedStep = ({
  token,
  outroMessage,
  candidateFeedbackEnabled,
}: {
  token: string
  outroMessage: string | null | undefined
  candidateFeedbackEnabled: boolean | null | undefined
}) => {
  const { t } = useTranslation()
  const [rating, setRating] = useState<'up' | 'down' | null>(null)
  const [text, setText] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [feedbackState, setFeedbackState] =
    useState<CandidateFeedbackState | null>(null)

  useEffect(() => {
    if (!candidateFeedbackEnabled) return

    setFeedbackState({ status: 'loading' })

    const fetchFeedback = async (): Promise<void> => {
      const res = await fetch(
        `${siteUrl}/interview/${token}/candidate-feedback`,
      )
      if (!res.ok) {
        setFeedbackState({ status: 'preparing' })
        return
      }
      const data: { enabled: boolean; scores?: CandidateCompetencyScore[] } =
        await res.json()
      if (!data.enabled) {
        setFeedbackState(null)
        return
      }
      if (!data.scores || data.scores.length === 0) {
        setFeedbackState({ status: 'preparing' })
        return
      }
      setFeedbackState({ status: 'ready', scores: data.scores })
    }

    fetchFeedback()
  }, [token, candidateFeedbackEnabled])

  const handleSubmitFeedback = async (
    selectedRating: 'up' | 'down',
  ): Promise<void> => {
    setRating(selectedRating)
    await fetch(`${siteUrl}/interview/${token}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rating: selectedRating,
        text: text.trim() || undefined,
      }),
    })
    setSubmitted(true)
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-6">
      <div className="text-center space-y-6 max-w-sm w-full">
        <div className="space-y-2">
          <h1 className="text-xl font-semibold">
            {t('candidate.completed.title')}
          </h1>
          {outroMessage ? (
            <div
              className="prose prose-sm text-muted-foreground max-w-none [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4"
              dangerouslySetInnerHTML={{ __html: outroMessage }}
            />
          ) : (
            <p className="text-muted-foreground">
              {t('candidate.completed.defaultOutro')}
            </p>
          )}
        </div>

        {feedbackState !== null && (
          <div className="text-left space-y-3 border rounded-lg p-4">
            <h2 className="text-sm font-semibold">
              {t('interviews.candidateFeedback.title')}
            </h2>
            {feedbackState.status === 'loading' ||
            feedbackState.status === 'preparing' ? (
              <p className="text-sm text-muted-foreground">
                {t('interviews.candidateFeedback.preparing')}
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                  {t('interviews.candidateFeedback.competencies')}
                </p>
                {feedbackState.scores.map((score) => (
                  <div key={score.competency} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {score.competency}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {t('interviews.candidateFeedback.scoreLabel', {
                          score: String(score.score),
                        })}
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className="bg-primary h-1.5 rounded-full"
                        style={{ width: `${(score.score / 10) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {score.rationale}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!submitted ? (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              {t('candidate.completed.feedbackQuestion')}
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => handleSubmitFeedback('up')}
                className="text-2xl hover:scale-110 transition-transform"
                aria-label="Good"
              >
                👍
              </button>
              <button
                onClick={() => handleSubmitFeedback('down')}
                className="text-2xl hover:scale-110 transition-transform"
                aria-label="Poor"
              >
                👎
              </button>
            </div>
            {rating !== null && (
              <div className="space-y-2">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t('candidate.completed.feedbackComment')}
                  rows={3}
                  maxLength={500}
                  className="w-full border rounded px-3 py-2 text-sm resize-none"
                />
                <button
                  onClick={() => handleSubmitFeedback(rating)}
                  className="w-full px-4 py-2 bg-primary text-primary-foreground rounded text-sm"
                >
                  {t('candidate.completed.feedbackSubmit')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('candidate.completed.feedbackThanks')}
          </p>
        )}
      </div>
    </div>
  )
}

const SEVERITY_VALUES = ['low', 'medium', 'high'] as const
type SeverityValue = (typeof SEVERITY_VALUES)[number]

const ErrorStep = ({ token }: { token: string }) => {
  const { t } = useTranslation()
  const [showForm, setShowForm] = useState(false)
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState<SeverityValue>('low')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmitReport = async () => {
    setSubmitting(true)
    await fetch(`${siteUrl}/interview/${token}/support-ticket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        topic: 'technical_issue',
        severity,
        subject: 'Interview error report',
        message: description.trim(),
      }),
    })
    setSubmitted(true)
    setSubmitting(false)
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-6">
      <div className="text-center space-y-4 max-w-sm">
        <div className="space-y-2">
          <h1 className="text-xl font-semibold">
            {t('candidate.error.title')}
          </h1>
          <p className="text-muted-foreground">{t('candidate.error.body')}</p>
        </div>

        {!submitted ? (
          !showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="text-sm underline text-muted-foreground hover:text-foreground"
            >
              {t('candidate.error.reportLink')}
            </button>
          ) : (
            <div className="space-y-3 text-left">
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {t('candidate.error.severityTitle')}
                </p>
                <Select
                  value={severity}
                  onValueChange={(v) => setSeverity(v as SeverityValue)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_VALUES.map((val) => (
                      <SelectItem key={val} value={val}>
                        {(t as (key: string) => string)(
                          `candidate.error.severity${val.charAt(0).toUpperCase()}${val.slice(1)}`,
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {t('candidate.error.describeTitle')}
                </p>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('candidate.error.describePlaceholder')}
                  rows={4}
                  maxLength={1000}
                  className="resize-none"
                />
              </div>
              <Button
                onClick={handleSubmitReport}
                disabled={!description.trim() || submitting}
                className="w-full"
              >
                {submitting
                  ? t('candidate.error.submitting')
                  : t('candidate.error.submit')}
              </Button>
            </div>
          )
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('candidate.error.submitted')}
          </p>
        )}
      </div>
    </div>
  )
}

const ChatInterviewView = ({ onLeave }: { onLeave: () => void }) => {
  const { t } = useTranslation()
  const { chatMessages, send } = useChat()
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  const handleSend = (): void => {
    const trimmed = inputValue.trim()
    if (!trimmed) return
    send(trimmed)
    setInputValue('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="flex flex-col h-svh">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {chatMessages.map((msg, i) => {
          const isCandidate = msg.from?.isLocal
          return (
            <div
              key={i}
              className={`flex ${isCandidate ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                  isCandidate
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : 'bg-muted text-foreground rounded-bl-sm'
                }`}
              >
                {msg.message}
              </div>
            </div>
          )
        })}
        <div ref={messagesEndRef} />
      </div>
      <div className="border-t p-4 flex gap-2 items-end">
        <Textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('candidate.room.chatPlaceholder')}
          rows={2}
          className="resize-none flex-1"
        />
        <div className="flex flex-col gap-2">
          <Button onClick={handleSend} disabled={!inputValue.trim()}>
            {t('candidate.room.chatSend')}
          </Button>
          <button
            onClick={onLeave}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            {t('candidate.room.chatLeave')}
          </button>
        </div>
      </div>
    </div>
  )
}

const InterviewRoomStep = ({
  token,
  participantToken,
  modality,
  onCompleted,
  onAbandoned,
  onError,
}: {
  token: string
  participantToken: string
  modality: 'voice' | 'text'
  onCompleted: () => void
  onAbandoned: () => void
  onError: () => void
}) => {
  const { t } = useTranslation()
  const tokenSource = useMemo(
    () => TokenSource.literal({ serverUrl: livekitWsUrl, participantToken }),
    [participantToken],
  )

  const room = useMemo(
    () =>
      new Room({
        audioCaptureDefaults: {
          ...(isKrispNoiseFilterSupported() && {
            processor: KrispNoiseFilter(),
          }),
        },
      }),
    [],
  )

  const [hasConnected, setHasConnected] = useState(false)
  const isAbandoningRef = useRef(false)

  const session = useSession(tokenSource, { room })
  const progress = useInterviewProgress(room)

  useEffect(() => {
    if (session.isConnected) {
      setHasConnected(true)
    }
  }, [session.isConnected])

  useEffect(() => {
    if (
      session.connectionState === ConnectionState.Disconnected &&
      !session.isConnected
    ) {
      if (isAbandoningRef.current) {
        onAbandoned()
      } else if (hasConnected) {
        onCompleted()
      } else {
        onError()
      }
    }
  }, [
    session.connectionState,
    session.isConnected,
    hasConnected,
    onCompleted,
    onAbandoned,
    onError,
  ])

  const handleLeave = (): void => {
    isAbandoningRef.current = true
    void fetch(`${siteUrl}/interview/${token}/abandon`, { method: 'POST' })
    session.end?.()
  }

  return (
    <AgentSessionProvider session={session}>
      {progress.currentQuestion !== null && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 border-b px-6 py-3 space-y-2">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {t('candidate.room.questionOf', {
                index: String(progress.currentQuestion.index + 1),
                total: String(progress.currentQuestion.total),
              })}
            </span>
            <span>
              {t('candidate.room.answered', {
                completed: String(progress.completedCount),
                total: String(progress.currentQuestion.total),
              })}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all duration-500"
              style={{
                width: `${(progress.completedCount / progress.currentQuestion.total) * 100}%`,
              }}
            />
          </div>
          <p className="text-sm font-medium truncate">
            {progress.currentQuestion.text}
          </p>
        </div>
      )}
      {modality === 'text' ? (
        <ChatInterviewView onLeave={handleLeave} />
      ) : (
        <>
          <main className="grid h-svh grid-cols-1 place-content-center">
            <AgentSessionView_01
              supportsVideoInput={false}
              supportsScreenShare={false}
              supportsChatInput={true}
              preConnectMessage={t('candidate.room.connecting')}
              onLeave={handleLeave}
              className="h-svh"
            />
          </main>
          <StartAudioButton label="Start Audio" />
        </>
      )}
    </AgentSessionProvider>
  )
}

const AbandonedStep = () => {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-center min-h-screen p-6">
      <div className="text-center space-y-2 max-w-sm">
        <h1 className="text-xl font-semibold">
          {t('candidate.abandoned.title')}
        </h1>
        <p className="text-muted-foreground">{t('candidate.abandoned.body')}</p>
      </div>
    </div>
  )
}

export const InterviewPage = () => {
  const { token } = useParams({ strict: false }) as { token: string }
  const { t, i18n } = useTranslation()
  const [pageState, setPageState] = useState<PageState>({ status: 'loading' })

  useEffect(() => {
    fetch(`${siteUrl}/interview/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          setPageState({ status: 'invalid' })
          return
        }
        const raw: InterviewPayload & { isOrgLocked?: boolean } =
          await res.json()
        const data: InterviewPayload & { isOrgLocked?: boolean } = {
          ...raw,
          branding: raw.branding ?? {
            primaryColor: null,
            logoUrl: null,
            website: null,
            tagline: null,
          },
        }

        if (
          raw.job.language !== 'both' &&
          SUPPORTED_LANGUAGES.has(raw.job.language)
        ) {
          void i18n.changeLanguage(raw.job.language)
        }

        if (data.isOrgLocked) {
          setPageState({ status: 'org_locked' })
          return
        }
        const firstStep = determineFirstStep(data)
        setPageState({ status: firstStep, payload: data })
      })
      .catch(() => setPageState({ status: 'invalid' }))
  }, [token, i18n])

  if (pageState.status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">{t('candidate.loading')}</p>
      </div>
    )
  }

  if (pageState.status === 'invalid') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-2">
          <h1 className="text-xl font-semibold">
            {t('candidate.invalid.title')}
          </h1>
          <p className="text-muted-foreground">{t('candidate.invalid.body')}</p>
        </div>
      </div>
    )
  }

  if (pageState.status === 'org_locked') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-2">
          <h1 className="text-xl font-semibold">
            {t('candidate.orgLocked.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('candidate.orgLocked.body')}
          </p>
        </div>
      </div>
    )
  }

  if (pageState.status === 'disqualified') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-2">
          <h1 className="text-xl font-semibold">
            {t('candidate.disqualified.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('candidate.disqualified.body')}
          </p>
        </div>
      </div>
    )
  }

  if (pageState.status === 'completed') {
    return (
      <CompletedStep
        token={token}
        outroMessage={pageState.outroMessage}
        candidateFeedbackEnabled={pageState.candidateFeedbackEnabled}
      />
    )
  }

  if (pageState.status === 'abandoned') {
    return <AbandonedStep />
  }

  if (pageState.status === 'error') {
    return <ErrorStep token={token} />
  }

  if (pageState.status === 'room') {
    return (
      <InterviewRoomStep
        token={token}
        participantToken={pageState.participantToken}
        modality={pageState.modality}
        onCompleted={() =>
          setPageState({
            status: 'completed',
            token,
            outroMessage: pageState.outroMessage,
            candidateFeedbackEnabled: pageState.candidateFeedbackEnabled,
          })
        }
        onAbandoned={() => setPageState({ status: 'abandoned' })}
        onError={() => setPageState({ status: 'error', token })}
      />
    )
  }

  const { payload } = pageState

  return (
    <div className="flex items-center justify-center min-h-screen p-6">
      <div className="w-full max-w-lg space-y-8">
        {pageState.status === 'screener' && (
          <ScreenerStep
            payload={payload}
            token={token}
            onPass={() => {
              if (payload.additionalInfoFields.length > 0) {
                setPageState({ status: 'additional-info', payload })
              } else {
                setPageState({ status: 'briefing', payload })
              }
            }}
            onDisqualified={() => setPageState({ status: 'disqualified' })}
          />
        )}

        {pageState.status === 'additional-info' && (
          <AdditionalInfoStep
            payload={payload}
            token={token}
            onNext={() => setPageState({ status: 'briefing', payload })}
          />
        )}

        {pageState.status === 'briefing' && (
          <BriefingStep
            payload={payload}
            token={token}
            onRoomReady={(roomName, participantToken, selectedModality) =>
              setPageState({
                status: 'room',
                roomName,
                participantToken,
                outroMessage: payload.job.outroMessage,
                candidateFeedbackEnabled: payload.job.candidateFeedbackEnabled,
                modality: selectedModality === 'chat' ? 'text' : 'voice',
              })
            }
            onError={() => setPageState({ status: 'error', token })}
          />
        )}
      </div>
    </div>
  )
}
